import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PAPayerType, MedicaidProgram } from '../../../../dtos/patientmr/authorizations.dto';
import { AlertPill } from '../../../common/Pills';
import { McoPicker } from '../components/McoPicker';

interface PayerSectionProps {
  /** Used to load the MCO list (systemConfig.payers.medicaidMcos) */
  patientId?: string;
  payerType: string;
  medicaidProgram: string;
  payerName: string;
  authNumber?: string;
  onChange: (field: string, value: string) => void;
  readOnly?: boolean;
  /** Existing PAs: the auth number is set by the approve action, not by edits (Phase 4). */
  authNumberLocked?: boolean;
  defaultOpen?: boolean;
}

const PAYER_OPTS = [
  { value: PAPayerType.MEDICAID, label: 'Medicaid' },
  { value: PAPayerType.PRIVATE, label: 'Private Insurance' }
];

const PROGRAM_OPTS = [
  { value: MedicaidProgram.STAR_KIDS, label: 'STAR Kids' },
  { value: MedicaidProgram.STAR_PLUS, label: 'STAR+PLUS' },
  { value: MedicaidProgram.FFS, label: 'Fee-for-Service (TMHP)' }
];

export const PayerSection: React.FC<PayerSectionProps> = ({
  patientId, payerType, medicaidProgram, payerName, authNumber = '', onChange, readOnly = false, authNumberLocked = false, defaultOpen = true
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const isMedicaid = payerType === PAPayerType.MEDICAID;
  const hasMco = isMedicaid && (medicaidProgram === MedicaidProgram.STAR_KIDS || medicaidProgram === MedicaidProgram.STAR_PLUS);

  const handlePayerTypeChange = (value: string) => {
    onChange('payerType', value);
    onChange('medicaidProgram', '');
    onChange('payerName', '');
  };

  const handleProgramChange = (value: string) => {
    onChange('medicaidProgram', value);
    onChange('payerName', '');
  };

  if (readOnly) {
    return (
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-slate-50" type="button">
          <div className="flex items-center gap-2">
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span className="text-sm font-semibold text-slate-800">Payer</span>
            <AlertPill type="default" text="Read-only" />
          </div>
        </button>
        {open && (
          <div className="px-4 pb-4 border-t border-slate-100 pt-3">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Payer Type</dt>
                <dd className="mt-0.5 text-sm text-slate-900">{isMedicaid ? 'Medicaid' : 'Private Insurance'}</dd>
              </div>
              {isMedicaid && medicaidProgram && (
                <div>
                  <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Program</dt>
                  <dd className="mt-0.5 text-sm text-slate-900">{PROGRAM_OPTS.find(p => p.value === medicaidProgram)?.label || medicaidProgram}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">{isMedicaid ? 'MCO' : 'Insurance Company'}</dt>
                <dd className="mt-0.5 text-sm text-slate-900">{payerName || '\u2014'}</dd>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-slate-50" type="button">
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="text-sm font-semibold text-slate-800">Payer</span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Payer Type <span className="text-red-400">*</span>
              </label>
              <select
                value={payerType}
                onChange={e => handlePayerTypeChange(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
              >
                <option value="">Select...</option>
                {PAYER_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {isMedicaid && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                  Program <span className="text-red-400">*</span>
                </label>
                <select
                  value={medicaidProgram}
                  onChange={e => handleProgramChange(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
                >
                  <option value="">Select...</option>
                  {PROGRAM_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            )}

            {hasMco && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                  MCO <span className="text-red-400">*</span>
                </label>
                <McoPicker patientId={patientId} program={medicaidProgram} value={payerName} onChange={v => onChange('payerName', v)} />
              </div>
            )}

            {payerType === PAPayerType.PRIVATE && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                  Insurance Company <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={payerName}
                  onChange={e => onChange('payerName', e.target.value)}
                  placeholder="e.g., BCBS, Aetna..."
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors"
                />
              </div>
            )}

            {payerType === PAPayerType.PRIVATE && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                  Authorization Number
                </label>
                <input
                  type="text"
                  value={authNumber}
                  onChange={e => onChange('authNumber', e.target.value)}
                  placeholder="Enter authorization number"
                  disabled={authNumberLocked}
                  aria-describedby={authNumberLocked ? 'pa-auth-number-hint' : undefined}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors disabled:bg-slate-100 disabled:text-slate-500"
                />
                {authNumberLocked && (
                  <p id="pa-auth-number-hint" className="mt-1 text-xs text-slate-500 text-left">
                    Recorded when the authorization is approved.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PayerSection;
